import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { AlertsType } from '@rappi/ui/alerts';
import { NewPurchaseOrderConfig } from '../../../../models/makers-finances-mgr.models';
import { MakersFinancesMgrService } from '../../../../services/makers-finances-mgr.service';
import { StoreAuxService } from '../../../../../services/storeAux/store-aux.service';
import { AlertsService } from '../../../../../services/alerts/alerts.service';
import { DetailMode, SourceType, SOURCE_TYPE, PO_FIELD, PO_SECTION_LABELS, PO_SECTION } from '../../purchase-order.constants';
import { PO_DOC_ERROR_TYPE, PO_DOC_FALLBACKS, PoDocErrorType } from './section-po-document.constants';
import {
  PoDocumentPreviewDialogComponent,
} from '../../po-document-preview-dialog/po-document-preview-dialog.component';

export interface UploadedDocumentValue {
  key: string;
  name: string;
  uploadedAt: string;
}

@Component({
  selector: 'app-section-po-document',
  templateUrl: './section-po-document.component.html',
  styleUrls: ['./section-po-document.component.scss'],
})
export class SectionPoDocumentComponent implements OnChanges {
  @Input() form: FormGroup;
  @Input() mode: DetailMode;
  @Input() sourceType: SourceType;
  @Input() remoteConfig: NewPurchaseOrderConfig | null;
  @Input() submitted = false;
  // Flips true once the parent has patched the form from the loaded PO. The document card is keyed
  // off uploadedFileName (not the form value), so we mirror the loaded doc name here.
  @Input() poLoaded = false;

  readonly sectionLabel = PO_SECTION_LABELS[PO_SECTION.PURCHASE_ORDER_DOCUMENT];

  readonly ERROR_TYPE = PO_DOC_ERROR_TYPE;

  uploadedFileName: string | null = null;
  uploading = false;
  fileErrorType: PoDocErrorType = null;
  fileBannerMessage: string | null = null;
  isPreviewing = false;

  constructor(
    private readonly financesService: MakersFinancesMgrService,
    private readonly storeAux: StoreAuxService,
    private readonly dialog: MatDialog,
    private readonly alerts: AlertsService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['poLoaded'] && this.poLoaded) {
      const doc = this.form.get(PO_FIELD.DOCUMENT_FILE)?.value as UploadedDocumentValue | null;
      if (doc?.name) {
        this.uploadedFileName = doc.name;
      }
    }
  }

  get isVisible(): boolean {
    return this.sourceType === SOURCE_TYPE.CLIENT;
  }

  get isFileRequired(): boolean {
    return this.submitted && !this.form.get(PO_FIELD.DOCUMENT_FILE)?.value;
  }

  get hasFileError(): boolean {
    return this.fileErrorType !== null || this.isFileRequired;
  }

  get bannerMessage(): string | null {
    if (this.fileErrorType) return this.fileBannerMessage;
    if (this.isFileRequired) {
      return this.remoteConfig?.purchase_order_documents?.error_messages?.invalid_format?.banner ?? PO_DOC_FALLBACKS.FILE_REQUIRED_BANNER;
    }
    return null;
  }

  onFileChange(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0] ?? null;
    this._setFile(file);
  }

  onFileDrop(event: DragEvent): void {
    event.preventDefault();
    const file = event.dataTransfer?.files?.[0] ?? null;
    this._setFile(file);
  }

  removeFile(): void {
    this.uploadedFileName = null;
    this.fileErrorType = null;
    this.fileBannerMessage = null;
    this.form.get(PO_FIELD.DOCUMENT_FILE)?.setValue(null);
  }

  openPreview(): void {
    const doc = this.form.get(PO_FIELD.DOCUMENT_FILE)?.value as UploadedDocumentValue | null;
    if (!doc?.key || this.isPreviewing) return;
    this.isPreviewing = true;
    this.financesService.previewFile(doc.key, this.storeAux.country).subscribe({
      next: blob => {
        this.isPreviewing = false;
        const url = URL.createObjectURL(blob);
        this.dialog.open(PoDocumentPreviewDialogComponent, {
          width: '95vw',
          maxWidth: '95vw',
          height: '90vh',
          panelClass: 'podp-panel',
          data: { url, name: doc.name, mimeType: blob.type },
        });
      },
      error: () => { this.isPreviewing = false; },
    });
  }

  private _setFile(file: File | null): void {
    if (!file) return;

    const docCfg = this.remoteConfig?.purchase_order_documents;
    const errMsgs = docCfg?.error_messages;
    const allowedMimes = docCfg?.rules?.allowed_mime_types ?? PO_DOC_FALLBACKS.ALLOWED_MIMES;
    const maxBytes = docCfg?.rules?.max_size_bytes;

    if (!allowedMimes.includes(file.type)) {
      this.fileErrorType = PO_DOC_ERROR_TYPE.INVALID_FORMAT;
      this.fileBannerMessage = errMsgs?.invalid_format?.banner ?? PO_DOC_FALLBACKS.INVALID_FORMAT_BANNER;
      this.alerts.openAlerts(errMsgs?.invalid_format?.alert ?? PO_DOC_FALLBACKS.INVALID_FORMAT_ALERT, AlertsType.error);
      return;
    }
    if (maxBytes && file.size > maxBytes) {
      this.fileErrorType = PO_DOC_ERROR_TYPE.SIZE_EXCEEDED;
      this.fileBannerMessage = errMsgs?.size_exceeded?.banner ?? PO_DOC_FALLBACKS.SIZE_EXCEEDED_BANNER;
      this.alerts.openAlerts(errMsgs?.size_exceeded?.alert ?? PO_DOC_FALLBACKS.SIZE_EXCEEDED_ALERT, AlertsType.error);
      return;
    }

    this.uploadedFileName = file.name;
    this.uploading = true;
    this.fileErrorType = null;
    this.fileBannerMessage = null;
    this.form.get(PO_FIELD.DOCUMENT_FILE)?.setValue(null);

    this.financesService.uploadFile(file, this.storeAux.country).subscribe({
      next: res => {
        this.uploading = false;
        const value: UploadedDocumentValue = {
          key: res.data.key,
          name: file.name,
          uploadedAt: new Date().toISOString(),
        };
        this.form.get(PO_FIELD.DOCUMENT_FILE)?.setValue(value);
        this.alerts.openAlerts(docCfg?.success_messages?.upload_complete ?? PO_DOC_FALLBACKS.UPLOAD_COMPLETE, AlertsType.success);
      },
      error: () => {
        this.uploading = false;
        this.fileErrorType = PO_DOC_ERROR_TYPE.UPLOAD_FAILED;
        this.fileBannerMessage = errMsgs?.upload_failed?.banner ?? PO_DOC_FALLBACKS.UPLOAD_FAILED_BANNER;
        this.uploadedFileName = null;
        this.form.get(PO_FIELD.DOCUMENT_FILE)?.setValue(null);
        this.alerts.openAlerts(errMsgs?.upload_failed?.alert ?? PO_DOC_FALLBACKS.UPLOAD_FAILED_ALERT, AlertsType.error);
      },
    });
  }
}
